import Intercom from "@intercom/messenger-js-sdk";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";

import { CircularProgress, CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import GlobalErrorSnackbar from "components/Common/GlobalErrorSnackbar";
import { PrimeReactProvider } from "primereact/api";
import { Helmet } from "react-helmet";
import { isLoggedIn } from "redux-store/dashboardActions";
import CustomSnackbar from "./components/Common/CustomSnackbar";
import { SnackbarProvider } from "./contexts/CustomSnackbarContext";
import { GuestUserProvider } from "./contexts/GuestUserContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import routes from "./routes";
import RouteRenderer from "./routes/routeRenderer";
import "./styles/App.scss";
import getTheme from "./theme";
// Root -> agents.qodex.ai redirect now handled at the Amplify level.
// import {
//   redirectRootToAgents,
//   shouldRedirectRootToAgents,
// } from "./utils/agentsRedirect";
import { identifyUser, initMixpanel } from "./utils/mixpanel";
import { getPageViewEvent } from "./utils/mixpanelEvents";
import { MixpanelService } from "./utils/mixpanelService";

function App({ user, selectedProject, selectedOrganisation }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === "production") {
      initMixpanel();
    }
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === "production" && user?.id) {
      identifyUser(user.id, {
        $email: user.email,
        $name: user.name,
      });
    }
  }, [user]);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== "production") return;
    MixpanelService.setContext({
      user_id: user?.id || null,
      user_name: user?.name || null,
      user_email: user?.email || null,
      project_id: selectedProject?.id || null,
      organisation_id: selectedOrganisation?.id || null,
      environment: process.env.REACT_APP_ENVIRONMENT || "",
    });
  }, [user?.id, user?.name, user?.email, selectedProject?.id, selectedOrganisation?.id]);

  useEffect(() => {
    if (user?.id) {
      Intercom({
        app_id: "h6ukw9wg",
        user_id: user.id, // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
        name: user.name, // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
        email: user.email, // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email
        created_at: user.created_at, // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date in a Unix timestamp (in seconds) e.g. 1704067200
      });
    } else {
      Intercom({
        app_id: "h6ukw9wg",
      });
    }
  }, [user]);

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== "production") return;
    const payload = getPageViewEvent(location.pathname);
    MixpanelService.track(payload.event_name, payload.properties);
  }, [location.pathname]);

  const queryParams = new URLSearchParams(location.search);
  const forkCollectionId = queryParams.get("fork-collection");
  if (forkCollectionId) {
    localStorage.setItem("forkCollectionId", forkCollectionId);
  }

  // Array of public routes that don't require authentication
  const publicRoutes = [
    "/login",
    "/",
    "/confirm-email",
    "/forgot-password",
    "/reset-password",
    "/auto-login",
  ];

  const validateUser = async () => {
    setIsLoading(true);

    const currentPath = location.pathname;

    // Root -> agents.qodex.ai redirect now handled at the Amplify level.
    // if (shouldRedirectRootToAgents(currentPath, location.search)) {
    //   redirectRootToAgents();
    //   return;
    // }

    if (currentPath === "/auto-login") {
      setIsLoading(false);
      return;
    }
    const response = await dispatch(isLoggedIn());
    const validated = response.payload;

    if (validated) {
      if (publicRoutes.includes(currentPath)) {
        window.location = "/quick-start";
      } else {
        setIsLoading(false);
      }
    } else {
      if (
        !publicRoutes.some(
          (route) =>
            currentPath === route ||
            (route === "/reset-password" &&
              currentPath.startsWith("/reset-password"))
        )
      ) {
        localStorage.clear();
        window.location = "/login";
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    validateUser();
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <SidebarProvider>
        <AppContent />
      </SidebarProvider>
    </ThemeProvider>
  );
}

const AppContent = () => {
  const { isDarkMode } = useTheme();
  const theme = getTheme(isDarkMode ? "dark" : "light");

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <PrimeReactProvider
        value={{
          ripple: true,
          unstyled: false,
          pt: { root: { className: isDarkMode ? "dark" : "" } },
        }}
      >
        <SnackbarProvider>
          <GuestUserProvider>
            <div className="textTitle bgPrimary text-[var(--text-primary)] overflow-auto">
              <Switch>
                {routes.map((route, index) => (
                  <RouteRenderer {...route} key={index} />
                ))}
                <Route
                  path="/terms_condition"
                  exact
                  render={() => {
                    window.location.href =
                      "https://www.qodex.ai/termsandconditions";
                    return null;
                  }}
                />
              </Switch>
              <Helmet>
                {process.env.REACT_APP_ENVIRONMENT !== "production" && (
                  <meta name="robots" content="noindex"></meta>
                )}
              </Helmet>
            </div>
          </GuestUserProvider>
          <CustomSnackbar />
          <GlobalErrorSnackbar />
        </SnackbarProvider>
      </PrimeReactProvider>
    </MuiThemeProvider>
  );
};

function mapStateToProps(state, ownProps) {
  const user = state.user.current_user || {};
  return {
    ...ownProps,
    user,
    selectedProject: state.common?.selectedProject ?? null,
    selectedOrganisation: state.common?.selectedOrganisation ?? null,
  };
}

export default connect(mapStateToProps)(App);
